import type { RootState } from 'reducers/root';
import {
  type UnknownAction,
  type PayloadAction,
  createAction,
  type EnhancedStore,
} from '@reduxjs/toolkit';
import type { PersistedState } from 'persistence/types';
import {
  isEqual,
  mergeDeepExceptArrays,
  omit,
  safeJsonParse,
} from 'persistence/utils';
import persistenceKey from 'persistence/persistenceKey';
import useWindowFocus from 'use-window-focus';
import { useAppDispatch } from 'hooks';
import { useEffect } from 'react';
import initialState from './initialState';

// Use this function to modify StorageEvent state before it gets merged with
// current application state:
function normalizeStorageEventStateBeforeMergingWithCurrentState(
  storageEventState: PersistedState,
): PersistedState {
  return omit(storageEventState, {
    main: {
      isMobile: undefined,
    },
  });
}

function hasChanges(event: StorageEvent): boolean {
  return !isEqual(
    normalizeStorageEventStateBeforeMergingWithCurrentState(
      safeJsonParse(event.oldValue),
    ),
    normalizeStorageEventStateBeforeMergingWithCurrentState(
      safeJsonParse(event.newValue),
    ),
  );
}

export const synchronizeState =
  createAction<PersistedState>('synchronizeState');

export const setLastActiveTabId = createAction<string>('setLastActiveTabId');

export const disablePersistence = createAction('disablePersistence');

// Will always return true when `persistence_enabled` is false:
function selectIsCurrentTabTheLastActiveOne(state: RootState): boolean {
  return state.config.lastActiveTabId === initialState.config.lastActiveTabId;
}

// Will always return true when `persistence_enabled` is false:
export function selectIsCurrentTabPrimary(state: RootState): boolean {
  return document.hasFocus() || selectIsCurrentTabTheLastActiveOne(state);
}

function isSynchronizeStateAction(
  action: UnknownAction,
): action is PayloadAction<PersistedState> {
  return action.type === String(synchronizeState);
}

export function persistenceReducer(
  state: RootState,
  action: UnknownAction,
): RootState {
  if (isSynchronizeStateAction(action)) {
    return mergeDeepExceptArrays(state, action.payload);
  }

  return state;
}

export function useSetLastActiveTabId(): void {
  const hasFocus = useWindowFocus();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (hasFocus) {
      dispatch(setLastActiveTabId(initialState.config.lastActiveTabId));
    }
  }, [hasFocus, dispatch]);
}

export function enablePersistenceBetweenTabs(
  store: EnhancedStore<RootState>,
): void {
  window.addEventListener('storage', event => {
    if (event.key === persistenceKey && hasChanges(event)) {
      const newState = safeJsonParse(event.newValue);
      const normalizedStorageEventState =
        normalizeStorageEventStateBeforeMergingWithCurrentState(newState);
      store.dispatch(synchronizeState(normalizedStorageEventState));
    }
  });

  // This will trigger a synchronization of the state on every new tab which will
  // cause only the new tab to be isCurrentTabPrimary before any widget interaction
  store.dispatch(setLastActiveTabId(initialState.config.lastActiveTabId));
}
